import { useMemo, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import Header from "../../components/Header";
import Menu from "../../components/Menu";
import Form from "../../../../../components/shared/Form";
import Filters from "./Filters";
import Settings from "./Settings";
import { useColumns } from "./columns";
import { useFiltersConfig } from "./Filters/useFiltersConfig";
import { useCompanyPreferences } from "../../hooks/usePreferences";
import { usePreferenceMutation } from "../../api/usePreferenceMutation";
import { useGroups } from "../../../General/OrganisationLevelGroups/context";
import { buildDefaultArray, buildDefaultFilters, flattenFilters } from "../../Timesheet/utils";

const options: string[] = [/**"columns",*/ "filters", "settings"];

function Team() {
    const [option, setOption] = useState("filters");
    const columns = useColumns();
    const teamFilters = useFiltersConfig();

    const { allocatedGroups } = useGroups();
    const scheduleTeam = useCompanyPreferences("scheduleTeam");
    const { mutate, isPending } = usePreferenceMutation("company", "scheduleTeam");

    const defaultColumns = useMemo(() => {
        return buildDefaultArray(columns, scheduleTeam?.columns ?? []);
    }, [columns, scheduleTeam]);

    const defaultFilters = useMemo(() => {
        return buildDefaultFilters(flattenFilters(teamFilters), scheduleTeam?.filters ?? []);
    }, [teamFilters, scheduleTeam]);

    const formDefaults = useMemo(() => ({
        columns: defaultColumns,
        filters: defaultFilters,
        defaultFilter: scheduleTeam?.defaultFilter ?? "",
        defaultPageSize: scheduleTeam?.defaultPageSize ?? 20,
        shiftCaseDisplay: scheduleTeam?.shiftCaseDisplay ?? "normal",
        colors: {
            regular: scheduleTeam?.colors?.regular ?? 0,
            approved: scheduleTeam?.colors?.approved ?? 0,
            submitted: scheduleTeam?.colors?.submitted ?? 0,
            locked: scheduleTeam?.colors?.locked ?? 0,
            unassigned: scheduleTeam?.colors?.unassigned ?? 0,
            openShift: scheduleTeam?.colors?.openShift ?? 0,
        },
        hideSummary: scheduleTeam?.hideSummary ?? false,
        hideUserProfile: scheduleTeam?.hideUserProfile ?? false,
        hideDailyInfo: scheduleTeam?.hideDailyInfo ?? false,
        hideSort: scheduleTeam?.hideSort ?? false,
        hidePrint: scheduleTeam?.hidePrint ?? false,
        shiftDisplay: {
            job: scheduleTeam?.shiftDisplay?.job ?? true,
            payCode: scheduleTeam?.shiftDisplay?.payCode ?? true,
            ...Object.fromEntries((allocatedGroups ?? []).map((group: any) => {
                const key = group.level.replaceAll("_", "");
                return [key, scheduleTeam?.shiftDisplay?.[key] ?? true];
            })),
        },
        unavailableDisplay: scheduleTeam?.unavailableDisplay ?? "block",
    }), [defaultColumns, defaultFilters, scheduleTeam, allocatedGroups]);

    const form = useForm({
        defaultValues: formDefaults,
        // `values` is deep-compared by RHF, so a new-but-equal `formDefaults`
        // reference (e.g. from a tab switch re-render) won't reset the form.
        // It only re-syncs when the DB data actually changes, and
        // `keepDirtyValues` keeps the user's in-progress edits.
        values: scheduleTeam === undefined ? undefined : formDefaults,
        resetOptions: { keepDirtyValues: true },
    });

    const onFinish = (value: typeof formDefaults) => mutate({ ...scheduleTeam, ...value });

    const panels: { key: string; node: ReactNode }[] = [
        { key: "filters", node: <Filters option="filters" /> },
        { key: "settings", node: <Settings /> },
    ];

    return (
        <div className="tw-p-5 tw-h-full tw-bg-white tw-rounded-lg">
            <Form className="tw-h-full" form={form} onFinish={onFinish}>
                <Header name="team" isLoading={isPending} />
                <Menu
                    options={options}
                    option={option}
                    setOption={setOption}
                />

                <div className="tw-px-7 tw-overflow-y-auto" style={{ height: `calc(100% - 100px)` }}>
                    {panels.map(({ key, node }) => (
                        <div
                            key={key}
                            className="tw-h-full"
                            style={{ display: option === key ? undefined : "none" }}
                        >
                            {node}
                        </div>
                    ))}
                </div>
            </Form>
        </div>
    );
}

export default Team;
